import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryItemAggregate } from '../../api/schema';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import { tagsDate } from '../../helpers/dataHelper';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getItemsRecoveredAggById, getItemsRecoveredAggByIdFetching, listItemsRecoveredAgg } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';

type ToolsLeftBehindAggregateProps = {
  dense?: boolean;
  title?: string;
  headerAddon?: ReactElement;
  withExport?: boolean;
  defaultPageSize?: number;
};
export const RecoveredJobs: React.FC<ToolsLeftBehindAggregateProps> = ({
  dense,
  title,
  headerAddon,
  withExport,
  defaultPageSize,
}: ToolsLeftBehindAggregateProps) => {
  const dispatch = useDispatch();

  const [params] = useSearchParams();
  const fetching = useSelector(getItemsRecoveredAggByIdFetching);
  const items = useSelector(getItemsRecoveredAggById(params));

  const [pagination, paginationToGet, paginationControl, paginationToGetAll] = useTablePagination({ paginationDirty: items.pagination });
  const itemsExport = useSelector(getItemsRecoveredAggById({ ...params, ...paginationToGetAll }));
  const totals = useMemo(() => ({ tools_left_behind_cnt: items.totals?.tools_left_behind_cnt ?? 0 }), [items.totals]);

  useEffect(() => {
    dispatch(listItemsRecoveredAgg({ ...params, ...paginationToGet }));
  }, [dispatch, params, paginationToGet]);

  const getExportData = useCallback(async () => {
    await dispatch(
      listItemsRecoveredAgg({
        ...params,
        ...paginationToGetAll,
      }),
    );
  }, [dispatch, params, paginationToGetAll]);

  const columns = useMemo(
    () => [
      {
        Header: 'Job Site',
        accessor: 'site_name',
        ellipsis: true,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => (
          <Link to={`/customers/job-sites/${row.original.site_id}/details`}>{row.original.site_name}</Link>
        ),
      },
      {
        // The client's own reference for the job; optional, so most rows are blank.
        Header: 'Job #',
        accessor: 'job_number',
        width: 90,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => row.original.job_number || '',
      },
      {
        Header: 'Location',
        accessor: 'address',
        ellipsis: true,
        minWidth: 160,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => [row.original.address, row.original.city, row.original.state].filter(Boolean).join(', '),
        getCellExportValue: (row: { original: QueryItemAggregate }) => [row.original.address, row.original.city, row.original.state].filter(Boolean).join(', '),
      },
      {
        Header: 'Delivered',
        accessor: 'first_visit',
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => tagsDate(row.original.first_visit, 'TIME_SIMPLE', 'DATE_MED'),
        getCellExportValue: (row: { original: QueryItemAggregate }) => tagsDate(row.original.first_visit, 'DATE_MED', 'DATE_MED'),
      },
      {
        Header: 'Recovered',
        accessor: 'last_visit',
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => tagsDate(row.original.last_visit, 'TIME_SIMPLE', 'DATE_MED'),
        getCellExportValue: (row: { original: QueryItemAggregate }) => tagsDate(row.original.last_visit, 'DATE_MED', 'DATE_MED'),
      },
      {
        Header: 'Count',
        accessor: 'tools_left_behind_cnt',
        width: '10%',
      },
    ],
    [],
  );

  return (
    <>
      {fetching && <LinearProgress />}

      <Table
        title={title}
        columns={columns}
        data={items.result || []}
        loading={fetching}
        toolbarDense="medium"
        size={dense ? 'small' : 'medium'}
        noGlobalFilter
        paginationControl={paginationControl}
        pagination={pagination}
        totals={totals}
        headerAddon={headerAddon}
        defaultPageSize={defaultPageSize}
        {...(withExport && { dataExport: itemsExport.result, getExportData, exportFileName: 'RecoveredJobs' })}
      />
    </>
  );
};
